import type { OrderingConfig } from 'api/locations'
import type { ConsolidatedLocation, LocationStatus } from './mockLocations'

// Map an OrderingConfig response row to the FE's ConsolidatedLocation shape.
// Two paths:
//   1. DATA-5223 is live and the operations-center-classified-errors flag is on
//      → config_errors_count is present, use it for severity classification.
//   2. Pre-DATA-5223 (master today) → fall back to config_errors.length.
//      No notice/error distinction without the classifier; treat all as errors.
// The two paths share the rest of the mapping so the FE can adopt the richer
// shape progressively without a coordinated deploy.
export function toConsolidatedLocation(
  row: OrderingConfig
): ConsolidatedLocation {
  return {
    id: row.id,
    // Some BE responses can omit `name` — keep the identifier visible
    // in headings / aria-labels / browser titles rather than rendering
    // a blank string.
    name: row.name ?? `Location #${row.id}`,
    street: row.street,
    city: row.city,
    state: row.state,
    zip: row.zip,
    orderingEnabled: row.ordering_status === 'enabled',
    // `not_configured` rows have no underlying provider config to
    // flip; consumers use this flag to disable the per-location
    // Enable CTA so the operator doesn't fire a no-op PUT.
    orderingConfigured: row.ordering_status !== 'not_configured',
    locationStatus: deriveLocationStatus(row),
  }
}

// Errors and notices are NOT exclusive on the underlying record — a
// location can carry both `config_errors_count.error > 0` and
// `config_errors_count.notice > 0` (NBC merchant 1059 demonstrates this in
// production). The Phase 2 row UX shows one status badge per row, so we
// collapse to a single `kind` with errors-first precedence: an operator's
// eye should land on the most actionable category, and zero-error rows
// surface their notice count once errors are cleared. The full
// per-severity breakdown lands on the location's drill-in page
// (DATA-4951) where there's room for both categories simultaneously.
function deriveLocationStatus(row: OrderingConfig): LocationStatus {
  const counts = row.config_errors_count
  if (counts) {
    if (counts.error > 0) return { kind: 'errors', count: counts.error }
    if (counts.notice > 0) return { kind: 'notices', count: counts.notice }
    return { kind: 'healthy' }
  }
  // Legacy fallback — DATA-5223 not active for this merchant yet.
  // All errors get the 'errors' bucket; severity classification arrives with
  // the operations-center-classified-errors flag.
  if (row.config_errors.length > 0) {
    return { kind: 'errors', count: row.config_errors.length }
  }
  return { kind: 'healthy' }
}
